import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatDate } from "@/lib/api";
import { AlertOctagon, Clock, HandCoins, ArrowUpRight } from "lucide-react";

export default function Calendar() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api
            .get("/calendar/due")
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div>
            <header className="border-b border-black/10 px-10 py-6">
                <div className="overline">Bölüm 05</div>
                <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">
                    Vade Takvimi
                </h1>
                <p className="text-sm text-neutral-500 mt-3">
                    Vadesi geçen faturalar, yaklaşan vadeler ve ödeme sözleri.
                </p>
            </header>

            {loading ? (
                <div className="p-10 font-mono text-xs uppercase text-neutral-500">Yükleniyor…</div>
            ) : !data ? null : (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-0 border-b border-black/10">
                    <Section
                        title="Vadesi Geçenler"
                        subtitle={`${data.overdue.length} kayıt`}
                        icon={AlertOctagon}
                        tone="red"
                        border="right"
                    >
                        {data.overdue.length === 0 ? (
                            <Empty text="Vadesi geçen fatura yok." />
                        ) : (
                            <ul className="divide-y divide-black/10" data-testid="cal-overdue">
                                {data.overdue.map((i) => (
                                    <li key={i.id} className="py-3 flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <Link to={`/musteriler/${i.customer_id}`} className="text-sm font-medium hover:text-brand-blue">
                                                {i.customer_name}
                                            </Link>
                                            <div className="text-xs font-mono text-neutral-500 mt-0.5">
                                                {i.invoice_no} · {formatDate(i.due_date)} ·{" "}
                                                <span className="text-brand-red">{i.days_overdue} gün gecikmiş</span>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-mono-num text-sm text-brand-red">
                                                ₺ {formatMoney(i.amount)}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Section>

                    <Section
                        title="Yaklaşan Vadeler"
                        subtitle="30 gün içinde"
                        icon={Clock}
                        tone="blue"
                        border="right"
                    >
                        {data.upcoming.length === 0 ? (
                            <Empty text="Yaklaşan vade yok." />
                        ) : (
                            <ul className="divide-y divide-black/10" data-testid="cal-upcoming">
                                {data.upcoming.map((i) => (
                                    <li key={i.id} className="py-3 flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <Link to={`/musteriler/${i.customer_id}`} className="text-sm font-medium hover:text-brand-blue">
                                                {i.customer_name}
                                            </Link>
                                            <div className="text-xs font-mono text-neutral-500 mt-0.5">
                                                {i.invoice_no} · {formatDate(i.due_date)} ·{" "}
                                                <span className="text-brand-blue">{i.days_left} gün kaldı</span>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-mono-num text-sm">₺ {formatMoney(i.amount)}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Section>

                    <Section
                        title="Ödeme Sözleri"
                        subtitle={`${data.promises.length} kayıt`}
                        icon={HandCoins}
                        tone="amber"
                    >
                        {data.promises.length === 0 ? (
                            <Empty text="Bekleyen ödeme sözü yok." />
                        ) : (
                            <ul className="divide-y divide-black/10" data-testid="cal-promises">
                                {data.promises.map((p) => (
                                    <li key={p.id} className="py-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <Link to={`/musteriler/${p.customer_id}`} className="text-sm font-medium hover:text-brand-blue">
                                                {p.customer_name}
                                            </Link>
                                            <span
                                                className={`text-xs font-mono ${p.is_past ? "text-brand-red" : "text-brand-blue"}`}
                                            >
                                                {formatDate(p.promised_date)}
                                            </span>
                                        </div>
                                        <p className="text-xs text-neutral-600 mt-1 line-clamp-2">{p.body}</p>
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">
                                            {p.author_name}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Section>
                </div>
            )}
        </div>
    );
}

function Section({ title, subtitle, icon: Icon, tone, border, children }) {
    const toneClass =
        tone === "red" ? "text-brand-red" : tone === "blue" ? "text-brand-blue" : tone === "amber" ? "text-brand-amber" : "";
    return (
        <div className={`p-8 ${border === "right" ? "xl:border-r border-black/10 border-b xl:border-b-0" : "border-b xl:border-b-0"}`}>
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="overline">{subtitle}</div>
                    <h2 className="font-display text-2xl font-bold tracking-tight mt-1">{title}</h2>
                </div>
                <Icon size={18} className={toneClass} />
            </div>
            {children}
        </div>
    );
}

function Empty({ text }) {
    return <div className="text-sm text-neutral-500 font-mono">{text}</div>;
}
